import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModule } from '../../contexts/ModuleContext';
import { Store, Truck, ChevronDown, Check, BarChart3 } from 'lucide-react';

const ModuleSwitcher = () => {
  const navigate = useNavigate();
  const { activeModule, setActiveModule, canChooseModule, getUserModules } = useModule();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const modules = [
    {
      id: 'retail',
      name: 'Retail',
      icon: Store,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
    },
    {
      id: 'wholesale',
      name: 'Wholesale',
      icon: Truck,
      color: 'text-emerald-500',
      bgColor: 'bg-emerald-500/10',
    },
    {
      id: 'accounting',
      name: 'Accounting',
      icon: BarChart3,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
    },
  ];

  const availableModules = getUserModules();
  const currentModule = modules.find(m => m.id === activeModule) || modules[0];
  const CurrentIcon = currentModule.icon;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Don't render if user can't choose modules
  if (!canChooseModule()) {
    return null;
  }

  const handleModuleSwitch = (moduleId) => {
    if (moduleId !== activeModule) {
      setActiveModule(moduleId);
      navigate(`/${moduleId}/dashboard`, { replace: true });
    }
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg
          ${currentModule.bgColor} ${currentModule.color}
          hover:opacity-80 transition-all duration-200
          focus:outline-none focus:ring-2 focus:ring-accent-gold focus:ring-offset-2
        `}
      >
        <CurrentIcon className="w-4 h-4" />
        <span className="font-medium text-sm hidden sm:inline">{currentModule.name}</span>
        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 py-1 z-50 animate-fade-in">
          <div className="px-3 py-2 border-b border-gray-100">
            <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">Switch Module</p>
          </div>
          {modules
            .filter(m => availableModules.includes(m.id))
            .map((module) => {
              const Icon = module.icon;
              const isActive = module.id === activeModule;
              
              return (
                <button
                  key={module.id}
                  onClick={() => handleModuleSwitch(module.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5
                    hover:bg-gray-50 transition-colors
                    ${isActive ? 'bg-gray-50' : ''}
                  `}
                >
                  <div className={`w-8 h-8 rounded-lg ${module.bgColor} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${module.color}`} />
                  </div>
                  <span className="flex-1 text-left text-sm font-medium text-gray-700">
                    {module.name}
                  </span>
                  {isActive && (
                    <Check className="w-4 h-4 text-emerald-500" />
                  )}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
};

export default ModuleSwitcher;
